/**
 * Servicio de historial de navegación persistido en cookie.
 * Almacena la pila de rutas visitadas para que "Volver" lleve a la ruta anterior real.
 * @module services/navigationHistoryService
 */

const COOKIE_NAME = 'migro_nav_history';
const MAX_ENTRIES = 30;
const MAX_PATH_LENGTH = 250;

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, maxAgeDays: number = 1): void {
  if (typeof document === 'undefined') return;
  const encoded = encodeURIComponent(value);
  // Cookie ~4KB; keep payload safe
  if (encoded.length > 3500) return;
  document.cookie = `${name}=${encoded}; path=/; SameSite=Lax; max-age=${maxAgeDays * 86400}`;
}

function loadStack(): string[] {
  try {
    const raw = getCookie(COOKIE_NAME);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is string => typeof p === 'string' && p.length > 0 && p.length <= MAX_PATH_LENGTH).slice(-MAX_ENTRIES);
  } catch {
    return [];
  }
}

function saveStack(stack: string[]): void {
  const trimmed = stack.slice(-MAX_ENTRIES);
  setCookie(COOKIE_NAME, JSON.stringify(trimmed));
}

/**
 * Añade una ruta al historial (solo si es distinta a la última).
 */
export function pushPath(path: string): void {
  const normalized = path.startsWith('/') ? path : '/' + path;
  const stack = loadStack();
  const last = stack[stack.length - 1];
  if (last === normalized) return;
  stack.push(normalized);
  saveStack(stack);
}

/**
 * Quita la ruta actual del historial y devuelve la anterior (a la que se debe volver).
 * Solo se elimina la página actual; la ruta anterior se mantiene en el stack.
 */
export function popAndGetPrevious(): string | null {
  const stack = loadStack();
  if (stack.length < 2) return null;
  stack.pop(); // quitar página actual
  const previous = stack[stack.length - 1] ?? null;
  saveStack(stack);
  return previous;
}

/**
 * Comprueba si hay una ruta anterior en el historial (se puede mostrar "Volver" con sentido).
 */
export function hasPrevious(): boolean {
  const stack = loadStack();
  return stack.length >= 2;
}

/**
 * Obtiene la ruta anterior sin modificar el historial (solo lectura).
 */
export function getPreviousPath(): string | null {
  const stack = loadStack();
  if (stack.length < 2) return null;
  return stack[stack.length - 2] ?? null;
}

export const navigationHistoryService = {
  pushPath,
  popAndGetPrevious,
  hasPrevious,
  getPreviousPath,
};
